/* eslint-disable no-param-reassign */
import queryBuilder from '@/helpers/search-client/query-builder/query';
import { isEmpty, debounce, ndIsNaN, deleteValuelessProperties, removeEmptyItems } from '@/helpers/utilities';
import { fieldsAreEmpty } from '@/helpers/search';
import searchClient from '@/helpers/search-client';
import { setDomContext, setWindowContext } from '@/helpers/context';
import typeMapper from '@/helpers/search-client/typeMapper';
import dynamicfilterOptions from '@/helpers/search-client/dynamicFilterOptions';
import {
  currentPriceFilterName,
  fullTextFilterName,
  financesTermFilterName,
  financesDepositFilterName,
  financesAnnualMileageFilterName,
  financesPaymentFilterName,
  financesProductFilterName,
  freeTextFilterName,
} from '@/helpers/search-client/filterNames';
import {
  availableOrderDirections,
  availableOrderFieldNames,
} from '@/helpers/search-client/orderByNames';

const mapFinances = (price, finance) => {
  const { percentageDeposit } = finance;
  if (!percentageDeposit) return finance;

  // if finance deposit is a percentage we need to add a fixed deposit value
  // this is used for other calulations and to display to the customer
  return {
    ...finance,
    deposit: (percentageDeposit / 100) * price.current,
  };
};

// map vehicles to make sure the veux vehicle state is the source of truth
const mapVehicles = (vehicle) => {
  const { finances } = vehicle;
  if (!finances) return vehicle;
  vehicle.finances = finances.map(mapFinances.bind(null, vehicle.price));
  return vehicle;
};

const getFinancesDataContext = (defaults, searchCriteria) => {
  const { depositType } = defaults;
  const {
    from: paymentFrom = null,
    to: paymentTo = null,
  } = searchCriteria?.finances?.payment || {};

  let {
    deposit,
    annualMileage,
    term,
    productType,
  } = searchCriteria?.finances || {};

  if (!term) term = defaults?.term;
  if (!deposit) deposit = defaults?.deposit;
  if (!annualMileage) annualMileage = defaults?.annualMileage;
  if (!productType) productType = defaults?.product;

  const options = {
    term,
    annualMileage,
  };

  if (productType) {
    options.productType = productType;
  }

  const payment = {};
  if (!['', null].includes(paymentFrom)) {
    payment.from = paymentFrom;
  }
  if (!['', null].includes(paymentTo)) {
    payment.to = paymentTo;
  }
  if (!fieldsAreEmpty(payment)) {
    options.payment = { ...payment };
  }

  // if the deposit type is percentage set the correct deposit field
  // and no user interaction has been made with the searchCriteria
  if (depositType === 'percentage' && !searchCriteria?.finances) {
    return {
      ...options,
      percentageDeposit: deposit,
    };
  }

  // return default desposit value
  return {
    ...options,
    deposit,
  };
};

const getNewFinancesDataContext = (defaults) => {
  if (defaults.length === 0) return null;

  return defaults.reduce((result, financeItemContext) => {
    const {
      deposit,
      annualMileage,
      term,
      depositType,
      conditionValue,
      product: productType,
    } = financeItemContext;

    const financeItem = {
      term,
      annualMileage,
      conditionValue: conditionValue || 'New',
      productType,
    };

    if (depositType === 'percentage') {
      financeItem.percentageDeposit = deposit;
    } else {
      financeItem.deposit = deposit;
    }

    result.push(deleteValuelessProperties(financeItem, {
      isRemoveEmptyString: true,
      isRemoveZero: true,
    }));

    return result;
  }, []);
};

const reduceArrayKeyValuePair = (array, key = '') => array.reduce((object, value) => {
  const clone = { ...object };
  // create a label based on the nested arrays
  const newKey = key ? `${value.label}|${key}` : value.label;
  clone[newKey] = value.count;

  // if there is a variants array then loop through again
  if (value.variants) {
    return { ...clone, ...reduceArrayKeyValuePair(value.variants, value.label) };
  }

  // if there is a models array then loop through again
  if (value.models) {
    return { ...clone, ...reduceArrayKeyValuePair(value.models, value.label) };
  }

  return clone;
}, {});

const prepareUpdatedFiltersCounts = (data) => {
  // create flat object from the count keys
  const flatObject = Object.keys({ ...data }).reduce(
    (object, key) => ({ ...object, ...data[key] }),
    {},
  );
  // create a flatter object with the search critera
  return Object.keys(flatObject).reduce((object, key) => {
    const value = flatObject[key];
    const clone = { ...object };
    if (!value) return object;

    // !TODO look into this so that nested items work better
    if (!value.items || 'max' in value) {
      clone[key] = value;
      return clone;
    }
    // if there are items in the object flatten this too for key pair values
    clone[key] = reduceArrayKeyValuePair(value.items);
    return clone;
  }, {});
};

const loopItemsArray = (array, haystack, key = '') => array.map((n) => {
  // loop through the items to update the count based on the key
  const newKey = key ? `${n.label}|${key}` : n.label;
  const updatedValue = haystack ? haystack[newKey] : null;
  const newValue = {
    ...n,
    count: updatedValue || 0,
  };

  // if we have a variants array loop again and update
  if (n.variants) {
    newValue.variants = loopItemsArray(n.variants, haystack, newKey);
  }

  if (n.models) {
    newValue.models = loopItemsArray(n.models, haystack, newKey);
  }

  return newValue;
});

const getNewDefaultValue = (current, min, max) => {
  if (Array.isArray(current)) {
    const sortedArr = [...current].sort((a, b) => a - b);
    return sortedArr.length > 0
      ? [
        getNewDefaultValue(sortedArr[0], min, max),
        getNewDefaultValue(sortedArr[sortedArr.length - 1], min, max),
      ]
      : [];
  }
  if (current < min) return min;
  if (current > max) return max;
  return current;
};

export default {
  clearSearchCriteria({ commit }) {
    // clear the search criteria being used
    commit('setValue', {
      key: 'searchCriteria',
      value: {},
    });
  },

  clearCurrentPage({ commit }) {
    // reset the current page to 1
    commit('setValue', {
      key: 'currentPage',
      value: 1,
    });
  },

  async setSortParams({ commit, dispatch, rootGetters }, { data, reload = true }) {
    // set the sort params when a user changes the value
    // and set the current page back to 1
    commit('setSortParams', { data, unit: rootGetters['ndc/app/getUnit'] });
    if (reload) await dispatch('getSortedVehicles');
  },

  async getCount({ commit, getters }) {
    const { searchCriteria, uuid, clientToken, apiUrl } = getters;

    const response = await searchClient.getCount({
      uuid,
      clientToken,
      apiUrl,
      criteria: searchCriteria,
    });

    commit('setValue', {
      key: 'foundVehiclesCount',
      value: response.data.getCount,
    });

    return response.data.getCount;
  },

  async getKeywordSearch({ getters }, keyword) {
    const { getDefaultScope: stockScope, uuid, clientToken, apiUrl } = getters;

    const response = await searchClient.getKeywordSearch({
      uuid,
      clientToken,
      apiUrl,
      keyword,
      stockScope,
    });

    const { getFilterAttributes } = response.data;

    return getFilterAttributes.manufacturerModel;
  },

  updateFiltersCounts: debounce(async ({ state, commit, getters }) => {
    // function to update the filter counts as a user filters the search criteria
    const { filtersOptions } = state;
    const { uuid, clientToken, apiUrl, getDefaultScope, searchCriteria } = getters;

    const response = await searchClient.getFiltersOptionsCounts({
      uuid,
      clientToken,
      apiUrl,
      criteria: searchCriteria,
      defaultCriteria: getDefaultScope,
    });

    const updatedCounts = prepareUpdatedFiltersCounts({ ...response.data });

    // a list of keys we do not need to update
    const blacklist = [
      financesTermFilterName,
      financesDepositFilterName,
      financesAnnualMileageFilterName,
      financesPaymentFilterName,
      currentPriceFilterName,
      fullTextFilterName,
      freeTextFilterName,
    ];

    // update the current values stored in vuex with the new counts
    const options = Object.keys({ ...filtersOptions })
      .filter((key) => blacklist.indexOf(key) === -1)
      .reduce((object, key) => {
        const cloned = { ...object };
        cloned[key] = { ...filtersOptions[key] };

        if (!('items' in cloned[key])) return cloned;

        if (!isEmpty(cloned[key].items)) {
          cloned[key].items = loopItemsArray(cloned[key].items, updatedCounts[key]);
        }

        return cloned;
      }, {});

    commit('setValue', {
      key: 'filtersOptions',
      value: { ...updatedCounts, ...options },
    });
  }, 500),

  // update the default values for finance filters in vuex store
  // base on its min, max
  updateFinanceFiltersDefaults: ({ commit, getters, state }, filtersOptions) => {
    const { financeDefaults } = getters;
    if (!financeDefaults) return;

    const financeFilterNamesMap = {
      term: financesTermFilterName,
      deposit: financesDepositFilterName,
      payment: financesPaymentFilterName,
      annualMileage: financesAnnualMileageFilterName,
      productName: financesProductFilterName,
    };

    const isPercentageDeposit = financeDefaults.depositType === 'percentage';
    const financesProductName = filtersOptions?.financesProductName?.items;
    const isValidFinanceProduct = financesProductName
      && financesProductName.findIndex((p) => p.label === financeDefaults.product) > -1;

    const newDefaultValues = Object.keys(financeDefaults).reduce((memo, key) => {
      // Should not update deposit value if depositType = 'percentage'
      if (key === 'deposit' && isPercentageDeposit) {
        return { ...memo, deposit: financeDefaults.deposit };
      }

      if (key === 'product' && !isValidFinanceProduct) {
        return { ...memo, product: '' };
      }

      // get new default value base on it's filtersOptions min and max
      const filterValues = filtersOptions[financeFilterNamesMap[key]];
      const newValue = getNewDefaultValue(
        financeDefaults[key],
        filterValues?.min,
        filterValues?.max,
      );
      return { ...memo, [key]: newValue };
    }, {});

    const newContext = { ...state.context };
    newContext.site.stockScope.usedFinanceDefaults = {
      ...financeDefaults,
      ...newDefaultValues,
    };

    commit('setValue', {
      key: 'context',
      value: newContext,
    });
  },

  getVehicles: debounce(async ({ commit, state, dispatch, getters }) => {
    if (state.isInitSearchLoading) return;
    // get the required vehicles from elastic search to display to the user
    commit('setValues', [
      {
        key: 'isSearchLoading',
        value: true,
      },
    ]);

    dispatch('clearCurrentPage');
    const { currentPage, sortParams, pageSize, fields } = state;
    const { searchCriteria, uuid, clientToken, apiUrl,
      getDefaultScope: stockScope, financeDefaults, newFinanceDefaults } = getters;

    const response = await searchClient.getVehiclesWithCount({
      stockScope,
      uuid,
      clientToken,
      apiUrl,
      criteria: searchCriteria,
      currentPage,
      pageSize,
      sortParams,
      fields,
      financesDataContext: getFinancesDataContext(financeDefaults, searchCriteria),
      newFinancesDataContext: getNewFinancesDataContext(newFinanceDefaults),
    });

    const maxPagesCount = Math.ceil(response.data.getCount / pageSize);

    commit('setValues', [
      {
        key: 'listingItems',
        value: response.data.allVehicles.map(mapVehicles),
      },
      {
        key: 'offerVehicles',
        value: response.data.offerVehicles,
      },
      {
        key: 'foundVehiclesCount',
        value: response.data.getCount,
      },
      {
        key: 'maxPages',
        value: maxPagesCount,
      },
      {
        key: 'isSearchLoading',
        value: false,
      },
    ]);
  }, 300),

  getRecommendedVehicles: async (
    { state, getters },
    { capIds, pageSize, currentPage, withCount },
  ) => {
    const filteredCapIds = !Array.isArray(capIds)
      ? []
      : removeEmptyItems(capIds);

    if (!filteredCapIds.length) {
      return { vehicles: [], total: 0 };
    }

    const { fields, sortParams } = state;
    const {
      uuid,
      clientToken,
      apiUrl,
      getDefaultScope: stockScope,
      financeDefaults,
      newFinanceDefaults,
      getStockScope,
      getSiteScope,
      getSectionScope,
    } = getters;

    const searchCriteria = {
      ...getStockScope,
      ...getSiteScope,
      ...getSectionScope,
      ...{ capId: filteredCapIds },
    };

    const searchVehicleFncName = withCount ? 'getVehiclesWithCount' : 'getVehicles';

    try {
      const response = await searchClient[searchVehicleFncName]({
        stockScope,
        uuid,
        clientToken,
        apiUrl,
        criteria: searchCriteria,
        currentPage,
        pageSize,
        sortParams,
        fields,
        financesDataContext: getFinancesDataContext(financeDefaults, searchCriteria),
        newFinancesDataContext: getNewFinancesDataContext(newFinanceDefaults),
      });

      const { allVehicles = [], offerVehicles = [], getCount = null } = response?.data || {};

      const financeVehicles = allVehicles.map(mapVehicles).map(((item) => {
        const { finances } = item;
        if (finances) {
          return {
            ...item,
            finances: finances.map(mapFinances.bind(null, item.price)),
          };
        }

        const offer = offerVehicles.find((n) => n.id === item.id);
        return { ...item, finances: offer?.finances || null };
      }));

      return { vehicles: financeVehicles, total: getCount };
    } catch (err) {
      console.log(err);
      return { vehicles: [], total: 0 };
    }
  },

  async getVehiclesWithPageSize({ commit, state, getters }) {
    // get all vehicles with the page size
    commit('setValues', [
      {
        key: 'isInitSearchLoading',
        value: true,
      },
    ]);

    const { currentPage, sortParams, pageSize, fields } = state;
    const totalPageSize = currentPage * pageSize;
    const {
      getDefaultScope: stockScope,
      searchCriteria,
      uuid,
      clientToken,
      apiUrl,
      financeDefaults,
      newFinanceDefaults,
    } = getters;

    const response = await searchClient.getVehiclesWithCount({
      stockScope,
      uuid,
      clientToken,
      apiUrl,
      criteria: searchCriteria,
      currentPage: 1,
      pageSize: totalPageSize,
      sortParams,
      fields,
      financesDataContext: getFinancesDataContext(financeDefaults, searchCriteria),
      newFinancesDataContext: getNewFinancesDataContext(newFinanceDefaults),
    });

    const maxPagesCount = Math.ceil(response.data.getCount / pageSize);

    commit('setValues', [
      {
        key: 'listingItems',
        value: response.data.allVehicles.map(mapVehicles),
      },
      {
        key: 'offerVehicles',
        value: response.data.offerVehicles,
      },
      {
        key: 'foundVehiclesCount',
        value: response.data.getCount,
      },
      {
        key: 'maxPages',
        value: maxPagesCount,
      },
      {
        key: 'isInitSearchLoading',
        value: false,
      },
    ]);
  },

  async loadMoreItems({ commit, state, getters }) {
    // load the next set of vehicles
    const {
      currentPage,
      sortParams,
      offerVehicles,
      pageSize,
      listingItems,
      fields,
    } = state;
    const { searchCriteria, uuid, clientToken, apiUrl,
      getDefaultScope: stockScope, financeDefaults, newFinanceDefaults } = getters;

    if (getters.isMaxPage) return true;

    const response = await searchClient.getVehicles({
      stockScope,
      uuid,
      clientToken,
      apiUrl,
      criteria: searchCriteria,
      currentPage: currentPage + 1,
      pageSize,
      sortParams,
      fields,
      financesDataContext: getFinancesDataContext(financeDefaults, searchCriteria),
      newFinancesDataContext: getNewFinancesDataContext(newFinanceDefaults),
    });

    const allVehicles = [...listingItems, ...response.data.allVehicles];
    const offers = [...offerVehicles, ...response.data.offerVehicles];

    commit('setValues', [
      {
        key: 'currentPage',
        value: currentPage + 1,
      },
      {
        key: 'listingItems',
        value: allVehicles.map(mapVehicles),
      },
      {
        key: 'offerVehicles',
        value: offers,
      },
    ]);

    return allVehicles;
  },

  // added debounce because the api is called two times continuously
  getSortedVehicles: debounce(async ({ commit, dispatch, getters, state }) => {
    if (state.isInitSearchLoading) return;
    dispatch('clearCurrentPage');
    const { sortParams, pageSize, currentPage, fields } = state;
    const { searchCriteria, uuid, clientToken, apiUrl,
      getDefaultScope: stockScope, financeDefaults, newFinanceDefaults } = getters;

    const response = await searchClient.getVehicles({
      stockScope,
      uuid,
      clientToken,
      apiUrl,
      criteria: searchCriteria,
      currentPage,
      pageSize,
      sortParams,
      fields,
      financesDataContext: getFinancesDataContext(financeDefaults, searchCriteria),
      newFinancesDataContext: getNewFinancesDataContext(newFinanceDefaults),
    });

    commit('setValue', {
      key: 'listingItems',
      value: response.data.allVehicles.map(mapVehicles),
    });
  }, 100),

  async getFilterOptions({ commit, dispatch, getters, state }) {
    if (state.isFiltersOptionsLoading) return;
    if (!isEmpty(state.filtersOptions)) return;

    commit('setValue', {
      key: 'isFiltersOptionsLoading',
      value: true,
    });

    const { getDefaultScope, uuid, clientToken, apiUrl } = getters;

    const response = await searchClient.getFiltersOptions({
      uuid,
      clientToken,
      apiUrl,
      criteria: getDefaultScope,
    });

    const { getFilterAttributes } = response.data;

    const isEmptyFilterAttributes = isEmpty(getFilterAttributes);

    // filter out the filter options which have data lengh <= 1
    const filtersOptions = !isEmptyFilterAttributes
      ? dynamicfilterOptions(getFilterAttributes)
      : {};

    commit('setValues', [
      {
        key: 'filtersOptions',
        value: filtersOptions,
      },
      {
        key: 'isFiltersOptionsLoading',
        value: false,
      },
    ]);

    if (!isEmptyFilterAttributes) {
      dispatch('updateFiltersCounts');
      dispatch('updateFinanceFiltersDefaults', getFilterAttributes);
    }
  },

  setAvailableFiltersOptions: debounce(async ({ commit, getters, state }) => {
    if (state.isFiltersOptionsLoading) return;
    if (!isEmpty(state.filtersOptions)) return;

    commit('setValue', {
      key: 'isFiltersOptionsLoading',
      value: true,
    });

    const { getDefaultScope, uuid, clientToken, apiUrl } = getters;

    const response = await searchClient.getFiltersOptions({
      uuid,
      clientToken,
      apiUrl,
      criteria: getDefaultScope,
    });

    commit('setValues', [
      {
        key: 'filtersOptions',
        value: response.data.getFilterAttributes,
      },
      {
        key: 'isFiltersOptionsLoading',
        value: false,
      },
    ]);
  }, 200),

  async getPostcodeCoordinates({ commit, rootState, getters }, { location }) {
    const { country } = rootState.ndc.app.context.site;
    const { uuid, clientToken, apiUrl } = getters;

    const response = await searchClient.getPostcodeCoordinates({
      uuid,
      clientToken,
      apiUrl,
      location,
      country,
    });

    commit('setValues', [
      {
        key: 'coordinates',
        value: response.data.getPostcodeCoordinates,
      },
      {
        key: 'location',
        value: location,
      },
    ]);
  },

  // load the critical search params when version is not available
  setCriticalSearch({ commit, dispatch, state }, searchParams) {
    if (!searchParams) return;
    const { version } = state;

    const whitelist = ['make', 'model', 'manufacturer'];
    const query = Object.entries(searchParams).reduce((acc, [k, v]) => {
      if (whitelist.indexOf(k) === -1) return acc;
      if (!v || isEmpty(v)) return acc;
      if (['make', 'manufacturer'].indexOf(k) !== -1) return { ...acc, manufacturer: v };
      if (k === 'model' && !Array.isArray(v)) return { ...acc, model: Object.values(v).flat() };
      if (k === 'model' && Array.isArray(v)) return { ...acc, model: v };
      return acc;
    }, {});

    if (isEmpty(query)) {
      queryBuilder.changeUrlParamsHelper({});
      return;
    }

    queryBuilder.changeUrlParamsHelper({
      updateSearchParams: query,
      updateVersion: version,
    });

    commit('setValue', {
      key: 'searchCriteria',
      value: query,
    });

    dispatch('ndc/app/TRACKING_EVENT', {
      event: 'SET_SEARCH_FILTERS',
      data: query,
    }, { root: true });
  },

  // load all search params from the URL
  setQueryToStoreSearchCritial({ commit, getters, dispatch, state }) {
    const { uuid, clientToken, apiUrl } = getters;
    const { version } = state;
    const values = [];
    const searchFilters = {};

    const {
      currentPage,
      sortParams,
      location,
      searchParams,
      nfcSearchVersion,
    } = searchClient.getQuerySearch({
      uuid,
      clientToken,
      apiUrl,
    });

    // if there is no search version then do not pass params
    if (!nfcSearchVersion || nfcSearchVersion !== version) {
      dispatch('setCriticalSearch', searchParams);
      return;
    }

    // update sortParams
    // only update if the param is not distance otherwise it will cause errors
    // due to waiting for the lat lon to be fetched
    if (sortParams !== undefined
      && availableOrderDirections.includes(sortParams?.[0]?.direction)
      && availableOrderFieldNames.includes(sortParams?.[0]?.fieldName)
      && sortParams?.[0]?.fieldName !== 'distance'
    ) {
      dispatch('setSortParams', { data: sortParams, reload: false });
    } else {
      queryBuilder.changeUrlParamsHelper({ updateSortParams: [], updateVersion: version });
    }

    // update coordinates
    if (!ndIsNaN(searchParams?.distance?.lat) && !ndIsNaN(searchParams?.distance?.lon)) {
      values.push({
        key: 'coordinates',
        value: {
          lat: searchParams.distance.lat,
          lon: searchParams.distance.lon,
        },
      });
    }

    // update location name
    if (location !== undefined) {
      // If there is a location
      values.push({
        key: 'location',
        value: location,
      });
      searchFilters.locationName = location;
    }

    // update current page
    if (currentPage !== undefined) {
      const mappedPage = /^[0-9]$/.test(currentPage) ? Number(currentPage) : 1;
      values.push({
        key: 'currentPage',
        value: mappedPage,
      });
    }

    // Update searchCriteria
    if (!isEmpty(searchParams)) {
      const value = typeMapper.mappingFunction(searchParams);
      values.push({
        key: 'searchCriteria',
        value,
      });
      Object.assign(searchFilters, { ...value });
    }

    if (values.length === 0) return;
    commit('setValues', values);
    dispatch('ndc/app/TRACKING_EVENT', {
      event: 'SET_SEARCH_FILTERS',
      data: searchFilters,
    }, { root: true });
  },

  setDefaultCriteria: ({ state, commit }) => {
    const { stockScope } = state.context.site;
    if (!stockScope || Object.keys(stockScope).length === 0) return;
    const { defaultValues } = stockScope;
    if (!defaultValues) return;

    const mergedSearchCriteria = { ...defaultValues, ...state.searchCriteria };

    Object.entries(mergedSearchCriteria).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length === 1 && value[0] === 'All') {
        delete mergedSearchCriteria[key];
      }
    });

    commit('setValues', [
      {
        key: 'defaultCriteria',
        value: defaultValues,
      },
      {
        key: 'searchCriteria',
        value: mergedSearchCriteria,
      },
    ]);
  },

  // function to set the components context in order below
  // - DOM
  // - WINDOW
  // debounce used so it does not trigger to many times as components load
  setContext: debounce(
    ({ commit, state, dispatch }) => {
      const { context } = state;
      const domContext = setDomContext(context);
      const windowContext = setWindowContext(context);
      const value = { ...context, ...domContext, ...windowContext };

      commit('setValue', {
        key: 'context',
        value,
      });

      dispatch('setDefaultCriteria');
    },
    100,
    { leading: true, trailing: false },
  ),

  afterModuleLoadedOnce({ dispatch }) {
    dispatch('setQueryToStoreSearchCritial');
    dispatch('setContext');
  },
};
